import { useCallback, useEffect, useState } from "react";
import {
  Banner,
  BlockStack,
  Button,
  InlineStack,
  Select,
  Text,
} from "@shopify/polaris";
import { importRequest } from "../../../../packages/shared/customer-import/client";
import type { QueueAccount } from "../../../../packages/shared/shopify-queue/client";
import { ImportControls, type ImportData } from "./ImportControls";
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
export function CustomerImportPanel({
  account,
  canImport,
  canSchedule,
}: {
  account: QueueAccount;
  canImport: boolean;
  canSchedule: boolean;
}) {
  const [data, setData] = useState<ImportData | null>(null),
    [error, setError] = useState(""),
    [pages, setPages] = useState<number[]>([]),
    [refresh, setRefresh] = useState(0),
    [connectionId, setConnectionId] = useState(""),
    cursor = pages[pages.length - 1];
  const reload = useCallback(() => {
    setPages([]);
    setRefresh((v) => v + 1);
  }, []);
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    void importRequest<ImportData>(
      base,
      account,
      "?" + new URLSearchParams({ ...connectionId ? { connection_id: connectionId } : {}, ...cursor ? { cursor: String(cursor) } : {} }),
      { signal: abort.signal },
    )
      .then((v) => {
        if (!abort.signal.aborted) setData(v);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, cursor, refresh, connectionId]);
  return (
    <BlockStack gap="400">
      <InlineStack gap="200">
        <Button onClick={reload}>Refresh imports</Button>
        <Button url="/shopify-updates">
          Publish stock and view Shopify updates
        </Button>
      </InlineStack>
      {error && <Banner tone="critical">{error}</Banner>}
      {!data && !error && <Text as="p">Loading customer imports…</Text>}
      {data && <Select label="Import store" value={data.selected_connection_id || ""}
        options={[{label: "Choose a store", value: ""}, ...data.stores.map(store => ({label: store.domain + (store.connected ? "" : " (disconnected)"), value: store.id}))]}
        onChange={id => {setData(null); setConnectionId(id); reload();}} />}
      {data && !data.selected_connection_id && data.stores.length > 1 && <Banner>Choose a store to view its imports and schedule.</Banner>}
      {data && (data.selected_connection_id || !data.stores.length) && (
        <ImportControls
          key={data.selected_connection_id || "none"}
          data={data}
          account={account}
          canImport={canImport}
          canSchedule={canSchedule}
          onSaved={reload}
        />
      )}
      <InlineStack gap="200">
        {!!pages.length && (
          <Button onClick={() => setPages((p) => p.slice(0, -1))}>
            Newer imports
          </Button>
        )}
        {data?.nextCursor && (
          <Button onClick={() => setPages((p) => [...p, data.nextCursor!])}>
            Older imports
          </Button>
        )}
      </InlineStack>
      {!!data?.legacySchedules.length && (
        <Banner tone="warning">
          Older schedule settings are retained for review. They had no working
          runner and have not been enabled here.
        </Banner>
      )}
    </BlockStack>
  );
}
